import { useCallback, useState } from 'react';

interface ICallModalWindowHandler<Params> {
  (params: Params): void;
}

interface ICloseModalWindowHandler {
  (): void;
}

interface IInactiveModalWindowProps {
  open: false;
  onClose: ICloseModalWindowHandler;
}

type TActiveModalWindowProps<Params> = Params & {
  open: true;
  onClose: ICloseModalWindowHandler;
};

export type TModalWindowProps<Params> = IInactiveModalWindowProps | TActiveModalWindowProps<Params>;

type TUseModalWindowWithParamsResult<Params> = [
  TModalWindowProps<Params>,
  ICallModalWindowHandler<Params>,
  ICloseModalWindowHandler,
];

/**
 * useModalWindowWithParams используется для управления модальными окнам с доп. параметрами
 * -
 * modalWindowProps - базовые для отображения мольного окна с кастомными доп. параметрами,
 * принимаемыми в функции вызова
 * -
 * callModalWindowHandler - функция вызова, принимает кастомные доп. параметры для
 * передачи в модальное окно.
 */
export function useModalWindowWithParams<Params extends object>(): TUseModalWindowWithParamsResult<Params> {
  const [open, setOpen] = useState<boolean>(false);
  const [params, setParams] = useState<Params | null>(null);

  const callModalWindowHandler = useCallback<ICallModalWindowHandler<Params>>(
    params => {
      setParams(params);
      setOpen(true);
    },
    [setOpen, setParams],
  );

  const closeModalWindowHandler = useCallback<() => void>(() => {
    setOpen(false);
    setParams(null);
  }, [setOpen, setParams]);

  const modalWindowProps: TModalWindowProps<Params> = {
    open,
    onClose: closeModalWindowHandler,
    ...(open && params ? params : {}),
  };

  return [modalWindowProps, callModalWindowHandler, closeModalWindowHandler];
}
